/**
 * Inserts a `_WxH` size suffix before an image URL's file extension so the
 * backend/CDN serves a right-sized asset instead of the full-resolution
 * original. Mirrors the portal's `resizeImageUrl` (apps/lms/portal/src/lib/
 * utils.ts) — duplicated here because the site-builder blocks package can't
 * import across the app boundary.
 *
 * `size` is a `"WxH"` string (e.g. `"1600x0"` for width-only). A `0`
 * dimension lets the other axis scale. `/static/` assets are returned
 * untouched, and an already-suffixed URL is left as-is (idempotent).
 *
 * @example
 * resizeImageUrl('https://cdn/x/cover.jpg', '1600x0')
 * // → 'https://cdn/x/cover_1600x0.jpg'
 */
export function resizeImageUrl(
  imageUrl: string | undefined,
  size: string,
  fallbackExtension?: string,
): string | undefined {
  if (!imageUrl) return undefined;
  if (imageUrl.includes('/static/')) return imageUrl;

  // images.unsplash.com (used by seed/demo content) has its own dynamic
  // resize API via query params — a `_WxH` path suffix would just 404.
  // Rewrite `w` (and drop any stale `h`, since we only ever pass a
  // width-only "WxH0" size) instead of falling through to the path logic.
  const unsplashWidth = size.match(/^(\d+)x0$/)?.[1];
  if (unsplashWidth) {
    try {
      const url = new URL(imageUrl);
      if (url.hostname === 'images.unsplash.com') {
        url.searchParams.set('w', unsplashWidth);
        url.searchParams.delete('h');
        return url.toString();
      }
    } catch {
      // Not a valid absolute URL — fall through to the path-suffix logic.
    }
  }

  const applyResize = (pathname: string) => {
    const dotIndex = pathname.lastIndexOf('.');
    if (dotIndex >= 0) {
      const filePath = pathname.slice(0, dotIndex);
      const extension = pathname.slice(dotIndex);
      if (filePath.endsWith(`_${size}`)) return pathname;
      return `${filePath}_${size}${extension}`;
    }
    if (!fallbackExtension) return pathname;
    return `${pathname}_${size}.${fallbackExtension.replace(/^\./, '')}`;
  };

  try {
    const url = new URL(imageUrl);
    url.pathname = applyResize(url.pathname);
    return url.toString();
  } catch {
    const match = imageUrl.match(/^([^?#]*)(\?[^#]*)?(#.*)?$/);
    if (!match) return imageUrl;
    const [, pathname = '', search = '', hash = ''] = match;
    return `${applyResize(pathname)}${search}${hash}`;
  }
}
